import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About Us" },
    { to: "/services", label: "Services" },
    { to: "/drivers", label: "Drivers" },
    { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const solid = scrolled || open;

    return (
        <header
            data-testid="main-navigation"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,padding,box-shadow] duration-300 ${
                solid ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"
            }`}
        >
            <div
                className={`mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 transition-[padding] duration-300 ${
                    scrolled ? "py-3" : "py-5"
                }`}
            >
                <Link
                    to="/"
                    data-testid="nav-logo-link"
                    className={`transition-colors duration-300 ${solid ? "text-night" : "text-white"}`}
                >
                    <Logo testId="nav-logo" className={scrolled ? "text-xs" : "text-sm"} />
                </Link>

                <nav aria-label="Primary" className="hidden lg:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `font-heading text-sm font-medium tracking-wide transition-colors duration-200 ${
                                    solid
                                        ? isActive
                                            ? "text-night"
                                            : "text-night/60 hover:text-night"
                                        : isActive
                                          ? "text-white"
                                          : "text-white/70 hover:text-white"
                                } ${isActive ? "underline decoration-brand decoration-2 underline-offset-8" : ""}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-5">
                    <Link
                        to="/contact"
                        data-testid="nav-contact-link"
                        className={`font-heading text-sm font-medium tracking-wide transition-colors duration-200 ${
                            solid ? "text-night/70 hover:text-night" : "text-white/80 hover:text-white"
                        }`}
                    >
                        Contact Us
                    </Link>
                    <Link
                        to="/drivers#apply"
                        data-testid="nav-drive-cta"
                        className="rounded-md bg-brand px-5 py-2.5 font-heading text-sm font-semibold tracking-wide text-night transition-[background-color,transform] duration-200 hover:bg-brand-dark hover:-translate-y-0.5"
                    >
                        Drive With Nightline
                    </Link>
                </div>

                <button
                    type="button"
                    data-testid="mobile-menu-button"
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                    onClick={() => setOpen((v) => !v)}
                    className={`lg:hidden rounded-md p-2 transition-colors ${solid ? "text-night" : "text-white"}`}
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            <div
                data-testid="mobile-menu"
                className={`lg:hidden overflow-hidden bg-white transition-[max-height] duration-300 ease-out ${
                    open ? "max-h-[480px] border-t border-night/10" : "max-h-0"
                }`}
            >
                <nav aria-label="Mobile" className="flex flex-col px-6 py-4">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `border-b border-night/5 py-4 font-heading text-base font-medium ${
                                    isActive ? "text-night" : "text-night/60"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/drivers#apply"
                        data-testid="mobile-drive-cta"
                        className="mt-4 mb-2 rounded-md bg-brand px-5 py-3.5 text-center font-heading text-base font-semibold text-night"
                    >
                        Drive With Nightline
                    </Link>
                </nav>
            </div>
        </header>
    );
};

export default Navbar;
